import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { dateTime, money, personName } from "@/lib/format";
import { useTill } from "@/lib/till-context";
import { Badge, Button, Input, Pager, cx, usePaged, useToast } from "@/components/ui";
import { Card, PageHead } from "@/components/Layout";

export type RefundReq = {
  id: string;
  number: string;
  saleNumber: string;
  customerName: string | null;
  summary: string;
  amount: number;
  method: string;
  reason: string | null;
  status: "waiting" | "approved" | "declined" | "cancelled" | "expired" | "done" | "closed";
  decidedBy: string | null;
  decidedAt: number | null;
  note: string | null;
  staffName: string;
  till: string;
  shop: string | null;
  createdAt: number;
  expiresAt: number;
};

const METHOD: Record<string, string> = { cash: "cash", momo: "Mobile Money", card: "card", credit: "store credit" };
const STATUS: Record<string, [string, "brand" | "amber" | "red" | "slate" | "dark"]> = {
  approved: ["Approved, not paid yet", "amber"],
  done: ["Paid back", "brand"],
  declined: ["Declined", "red"],
  closed: ["Declined", "red"],
  cancelled: ["Cancelled at the till", "slate"],
  expired: ["Ran out of time", "slate"],
};
const ago = (t: number) => {
  const m = Math.max(0, Math.round((Date.now() - t) / 60_000));
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`;
};

/** How many refunds are waiting, for the menu badge. */
export function useWaitingRefunds() {
  return useQuery({ queryKey: ["refund-requests"], queryFn: () => api<{ waiting: RefundReq[]; recent: RefundReq[] }>(till("/admin/refund-requests")), refetchInterval: 15_000 });
}

/** Refunds a cashier started at the till, waiting for someone to approve or decline them. */
export function Refunds() {
  const toast = useToast();
  const qc = useQueryClient();
  const { me } = useTill();
  const can = me?.can ? !!me.can.refunds : true;
  const q = useWaitingRefunds();
  const [declining, setDeclining] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const waiting = q.data?.waiting ?? [];
  const recent = usePaged(q.data?.recent ?? []);
  const decide = async (r: RefundReq, act: "approve" | "decline") => {
    if (act === "decline" && !note.trim()) return toast("Say why, so the cashier can tell the customer", "err");
    setBusy(r.id);
    try {
      await api(till(`/admin/refund-requests/${r.id}/${act}`), { body: { note } });
      toast(act === "approve" ? `${r.number} approved. ${r.till} can give the money back now` : `${r.number} declined. ${r.till} sees your reason`);
      setDeclining(null);
      setNote("");
      void qc.invalidateQueries({ queryKey: ["refund-requests"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      <PageHead title="Refunds" sub="Cashiers start refunds at the till. Approve or decline them here, from anywhere. Approved refunds are paid at the till." />
      <Card title={`Waiting for approval${waiting.length ? ` (${waiting.length})` : ""}`}>
        {q.isLoading ? <p className="px-5 pb-5 text-sm text-slate-500">Loading…</p> : null}
        {waiting.map((r) => (
          <div key={r.id} className="grid gap-3 border-t border-slate-100 px-5 py-4 md:grid-cols-[minmax(0,1fr)_170px_auto] md:items-center">
            <div className="min-w-0">
              <b className="block text-[15px]">{r.number} · {r.summary}</b>
              <span className="block text-[13px] text-slate-500">Sale {r.saleNumber}{r.customerName ? ` · ${r.customerName}` : ""}{r.reason ? ` · Reason: ${r.reason}` : ""}</span>
              <span className="block text-[13px] text-slate-500">Started by {personName(r.staffName)} at {[r.shop, r.till].filter(Boolean).join(" · ")} · {ago(r.createdAt)}</span>
            </div>
            <div>
              <b className="block text-[22px] font-extrabold">{money(r.amount)}</b>
              <span className="text-xs text-slate-500">Back by {METHOD[r.method] ?? r.method}</span>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {can ? (
                <>
                  <Button onClick={() => { setDeclining(declining === r.id ? null : r.id); setNote(""); }} className="!text-red-700">Decline</Button>
                  <Button tone="primary" disabled={busy === r.id} onClick={() => void decide(r, "approve")}>{busy === r.id ? "Saving…" : "Approve"}</Button>
                </>
              ) : <span className="text-xs text-slate-500">Your role can't approve refunds</span>}
            </div>
            {declining === r.id ? (
              <div className="flex flex-wrap gap-2 md:col-span-3">
                <Input autoFocus value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="Why? The cashier sees this" className="min-w-0 flex-1" />
                <Button disabled={busy === r.id} onClick={() => void decide(r, "decline")} className="!text-red-700">Decline refund</Button>
              </div>
            ) : null}
          </div>
        ))}
        {q.data && !waiting.length ? <p className="border-t border-slate-100 px-5 py-8 text-center text-sm text-slate-500">Nothing waiting. Refunds a cashier can't approve show up here.</p> : null}
      </Card>
      <Card title="Recent" className="mt-4">
        {recent.rows.map((r) => {
          const [label, tone] = STATUS[r.status] ?? [r.status, "slate"];
          return (
            <div key={r.id} className="grid gap-2 border-t border-slate-100 px-5 py-3.5 md:grid-cols-[minmax(0,1fr)_140px_240px] md:items-center">
              <div className="min-w-0">
                <b className="block text-sm">{r.number} · {r.summary}</b>
                <span className="block text-xs text-slate-500">Sale {r.saleNumber} · started by {personName(r.staffName)} · {dateTime(r.createdAt)}</span>
              </div>
              <b className="text-sm">{money(r.amount)}</b>
              <div className={cx("text-left md:text-right")}>
                <Badge tone={tone}>{label}</Badge>
                {r.decidedBy ? <span className="mt-1 block text-xs text-slate-500">by {r.decidedBy}{r.note ? ` · "${r.note}"` : ""}</span> : null}
              </div>
            </div>
          );
        })}
        {q.data && !(q.data.recent ?? []).length ? <p className="border-t border-slate-100 px-5 py-6 text-center text-sm text-slate-500">No refund requests yet.</p> : null}
        <Pager page={recent.page} size={recent.size} total={recent.total} onPage={recent.setPage} />
      </Card>
    </>
  );
}
